import { masteryBand } from './scoring';
import { cn } from '../../lib/cn';

const BAND_TEXT = {
  low: 'text-destructive',
  mid: 'text-warning-foreground',
  high: 'text-success',
  none: 'text-foreground',
};

// Hover/click popover for a disc node (depth 1=Area, 2=Competency, 3=Skill).
// Portaled to <body>, so it self-scopes `.ds`. Design system.
export default function ClassDiscPopover(props) {
  const name = props?.name || '';
  const depth = props?.depth;
  const meta = props?.classMeta;
  const isClickable = props?.isClickable;
  // Optional, default off (console keeps its current popover untouched):
  // suppresses the "N of M specific skills graded" ratio on Area/Competency
  // rings, keeping only the outer Skill ring's count — the inner rings' raw
  // avg-mastery % already says enough without also showing a count.
  const compactInnerRings = props?.compactInnerRings && depth !== 3;

  let levelLabel = '';
  let subtitle = '';
  let mainStat = null;
  let countDetail = '';
  let extraStat = null;

  if (depth === 1) {
    levelLabel = 'Area';
    subtitle = meta ? `${meta.n_competencies} competencies · ${meta.n_skills} skills` : '';
    if (meta?.avg_mastery != null) {
      mainStat = { value: `${Math.round(meta.avg_mastery * 100)}%`, label: 'avg mastery' };
      countDetail = `${meta.n_graded_skills} of ${meta.n_specific_skills} skill developmental descriptors graded`;
    } else {
      countDetail = 'Not yet graded';
    }
  } else if (depth === 2) {
    levelLabel = 'Competency';
    subtitle = meta?.area_name || '';
    if (meta?.avg_mastery != null) {
      mainStat = { value: `${Math.round(meta.avg_mastery * 100)}%`, label: 'avg mastery' };
      countDetail = `${meta.n_skills} skill${meta.n_skills === 1 ? '' : 's'} · ${meta.n_graded_skills} of ${meta.n_specific_skills} skill developmental descriptors graded`;
    } else if (meta?.n_skills) {
      countDetail = `${meta.n_skills} skill${meta.n_skills === 1 ? '' : 's'} · not yet graded`;
    }
  } else if (depth === 3) {
    levelLabel = 'Skill';
    subtitle = meta ? `${meta.area_name} · ${meta.competency_name}` : '';
    if (meta?.avg_mastery != null) {
      mainStat = { value: `${Math.round(meta.avg_mastery * 100)}%`, label: 'avg mastery' };
      countDetail = `${meta.n_specific_skills} skill developmental descriptor${meta.n_specific_skills === 1 ? '' : 's'} · ${meta.n_graded_skills} graded`;
      if (meta.avg_class_avg != null) {
        extraStat = `Class average: ${meta.avg_class_avg.toFixed(1)} / 5`;
      }
    } else {
      countDetail = `${meta?.n_specific_skills || 0} skill developmental descriptor${(meta?.n_specific_skills || 0) === 1 ? '' : 's'} · not yet graded`;
    }
  }

  if (compactInnerRings) countDetail = '';

  const band = meta?.avg_mastery != null ? masteryBand(meta.avg_mastery) : 'none';

  return (
    <div className="ds w-64 rounded-xl border border-border bg-popover p-3 text-popover-foreground shadow-xl">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {levelLabel}
      </div>
      <div className="text-sm font-bold text-foreground">{name}</div>
      {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
      {mainStat && (
        <div className="mt-1.5 flex items-baseline gap-1.5">
          <span className={cn('text-xl font-extrabold tabular-nums', BAND_TEXT[band])}>
            {mainStat.value}
          </span>
          <span className="text-[11px] text-muted-foreground">{mainStat.label}</span>
        </div>
      )}
      {countDetail && <div className="mt-1 text-[11px] text-muted-foreground">{countDetail}</div>}
      {extraStat && <div className="text-[11px] text-muted-foreground">{extraStat}</div>}
      {isClickable && (
        <div className="mt-2 text-[11px] font-semibold text-primary">
          Click to see skill developmental descriptors →
        </div>
      )}
    </div>
  );
}
